const LOSSY_TYPES = ['image/jpeg', 'image/webp'];
const COMPRESSIBLE_TYPES = [...LOSSY_TYPES, 'image/png'];
const MIN_QUALITY = 0.7;
const MAX_QUALITY = 0.95;
const QUALITY_STEPS = 6;
const MAX_SCALE_ATTEMPTS = 6;

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

/**
 * Returns the file untouched if it already fits `maxBytes`. Otherwise shrinks it to just under the limit:
 * for JPEG/WebP it picks the highest quality that fits, and only downscales dimensions when quality
 * alone isn't enough (always the case for PNG). Falls back to the original file if anything fails.
 */
export async function compressImageToLimit(file: File, maxBytes: number): Promise<File> {
  if (file.size <= maxBytes || !COMPRESSIBLE_TYPES.includes(file.type)) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return file;
      }

      const encode = (scale: number, quality: number): Promise<Blob | null> => {
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        return canvasToBlob(canvas, file.type, quality);
      };

      const isLossy = LOSSY_TYPES.includes(file.type);
      let scale = 1;
      let result: Blob | null = null;

      for (let attempt = 0; attempt < MAX_SCALE_ATTEMPTS && !result; attempt++) {
        let smallest = await encode(scale, isLossy ? MIN_QUALITY : 1);
        if (!smallest || smallest.type !== file.type) {
          break;
        }

        if (smallest.size <= maxBytes) {
          if (isLossy) {
            // Binary search for the highest quality that still fits.
            let best = smallest;
            let low = MIN_QUALITY;
            let high = MAX_QUALITY;
            for (let i = 0; i < QUALITY_STEPS; i++) {
              const mid = (low + high) / 2;
              const blob = await encode(scale, mid);
              if (blob && blob.size <= maxBytes) {
                best = blob;
                low = mid;
              } else {
                high = mid;
              }
            }
            smallest = best;
          }
          result = smallest;
        } else {
          // Size roughly scales with pixel count, so shrink each side by sqrt of the ratio (with a small margin).
          scale *= Math.max(0.4, Math.sqrt(maxBytes / smallest.size) * 0.95);
        }
      }

      if (!result) {
        return file;
      }
      return new File([result], file.name, { type: result.type, lastModified: Date.now() });
    } finally {
      bitmap.close();
    }
  } catch (err) {
    console.error('Error compressing image:', err);
    return file;
  }
}
